/**
 * ZD goods on several trucks (Addendum A of
 * docs/decisions/2026-09-30-bez-palet-podzial-transportu-po-wartosci.md).
 *
 * The transport form lets the operator state how much of a ZD line THIS truck
 * carries. Only entered rows are stored (`supplierCargoItems`). Rule per ZD
 * and truck (owner decision 2026-10-01, mirror of
 * backend/src/utils/supplierTruckCargo.js):
 * - the truck has >= 1 entered quantity for the ZD → that is the full list:
 *   a line without an entry is NOT on this truck ("0 — nie jedzie tym autem");
 * - nothing entered for the ZD → the truck carries the rest of every line
 *   (line − what the other trucks carry, entered and their own rest); two or
 *   more such trucks → each counts the whole rest + warning.
 * „Na innych transportach” = what the other trucks actually carry, their rest
 * included. This module shapes the editor rows, the live state, the payload
 * and the create-mode availability positions. Quantities only, no prices.
 */
import type { SupplierOrderDto } from '../../dto/supplierOrder';
import type {
  TransportSupplierCargoContextDto,
  TransportSupplierOrderCarriedDto,
  TransportSupplierOrderLoadItemDto,
  TransportSupplierOrderSummaryDto,
} from '../../types/transport';
import {
  getQuantitySetValue,
  isAllocationUnit,
  resolveStoredQuantitySet,
  roundCommercialQuantity,
  scaleQuantitySetToAmount,
  subtractQuantitySets,
  sumQuantitySets,
  type QuantitySet,
} from '../../utils/commercialQuantity';
import { normalizeUnitCode } from '../../utils/units';
import { getSupplierOrderSummary } from '../orders/components/SupplierOrderSummaryPreview';
import { formatProductDimensions } from '../../utils/productDimensions';
import { CARGO_QUANTITY_TOLERANCE } from '../../utils/transportCargoAllocation';
import type { AllocationKeyPosition } from '../../utils/transportCostAllocation';
import type { SupplierCargoItemPayload } from '../../components/sharedTransportFormUtils';

export type SupplierCargoUnit = 'pcs' | 'm2' | 'm3';
const UNITS: SupplierCargoUnit[] = ['pcs', 'm2', 'm3'];

export type CarriedSource = 'EXPLICIT' | 'REMAINDER' | 'AMBIGUOUS' | 'NONE';

/** One ZD line in the editor. */
export interface SupplierCargoLine {
  supplierOrderId: string;
  supplierOrderLabel: string;
  itemId: string;
  /**
   * 1-based position of the line in its ZD („poz. N”): the persisted
   * lineNumber — the same field the backend's unassigned-line warning uses —
   * else the place in the ZD's item order.
   */
  position?: number | null;
  /** Catalog variant of the line: the ZK lines of the same variant may pair with it. */
  productVariantId?: string | null;
  /**
   * ZK the ZD was generated from („Utwórz szkice ZD”, `draftSourceOrderId`):
   * the line's goods are that customer's (Tomek 2026-10-21).
   */
  sourceOrderId?: string | null;
  name: string;
  dimensions: string | null;
  /** The line's own unit — what the editor shows first. */
  unit: SupplierCargoUnit | null;
  /** Units the operator may type in (the line's set has them). */
  units: SupplierCargoUnit[];
  /** Unit the szt. key checks (purchase unit of the line, else its unit). */
  keyUnit: string | null;
  ordered: QuantitySet;
  /** Σ quantities entered for the line on the OTHER trucks; `null` = unknown. */
  otherExplicit: QuantitySet | null;
  /** Other trucks of this ZD with nothing entered for it: they carry the rest. */
  otherRestTrucks: number;
  /** Saved explicit quantity of THIS truck (edit mode), else null. */
  savedExplicit: { quantity: number; unit: SupplierCargoUnit } | null;
  /** Server quantity after delivery shortfall; independent of the editor entry. */
  savedCarried?: QuantitySet | null;
  width: number | null;
  height: number | null;
  length: number | null;
  thickness: number | null;
  /** Catalog „Liczymy tylko w sztukach”: no m² / m³ for the key mirror. */
  countInPiecesOnly: boolean;
  /** Catalog weight of one piece (kg); null = unknown (the trailer view counts 0). */
  pieceWeightKg?: number | null;
}

/** Operator input of one line (touched lines only). `null` = restore default. */
export interface SupplierCargoEntry {
  quantity: number | null;
  unit: SupplierCargoUnit;
}

/**
 * The rule context of one ZD for this truck: the server's `cargoContext`
 * (backend utils/supplierTruckCargo.buildSupplierCargoContext) — other
 * trucks, whether they list the ZD, Σ their entries per line.
 */
export interface SupplierCargoZdContext {
  /** Another non-cancelled INBOUND truck carries this ZD (besides this one). */
  onOtherTruck: boolean;
  /** Labels of those trucks. */
  otherTruckLabels: string[];
  /** Labels of the other trucks with nothing entered for this ZD (the warning). */
  restTruckLabels: string[];
  /** Per line: Σ quantities entered on the other trucks, line unit (absent = 0). */
  otherExplicitByItemId: Record<string, TransportSupplierOrderCarriedDto>;
  /** Catalog weight of one piece per line (kg), when the server sent it. */
  pieceWeightKgByItemId?: Record<string, number>;
}

/** `cargoContext` DTO → the resolver's context; `null` stays unknown. */
export function supplierCargoContextFromDto(
  dto: TransportSupplierCargoContextDto | null | undefined,
): SupplierCargoZdContext | null {
  if (!dto) return null;
  return {
    onOtherTruck: dto.otherTrucks.length > 0,
    otherTruckLabels: dto.otherTrucks.map((truck) => truck.label),
    restTruckLabels: dto.otherTrucks.filter((truck) => !truck.listed).map((truck) => truck.label),
    otherExplicitByItemId: dto.otherExplicitByItemId,
    ...(dto.pieceWeightKgByItemId ? { pieceWeightKgByItemId: dto.pieceWeightKgByItemId } : {}),
  };
}

/** Live state of one ZD line on THIS truck (the editor and the key mirror). */
export interface SupplierCargoLineState {
  /** What this truck carries of the line. */
  carried: QuantitySet;
  /** What the other trucks carry, their rest included; `null` = unknown. */
  onOtherTrucks: QuantitySet | null;
  /** Not assigned to any truck („Pozostało”); `null` = unknown. */
  unassigned: QuantitySet | null;
  /** The ZD is listed on this truck and this line has no entry. */
  notOnTruck: boolean;
  /** Nothing entered here while another truck also carries the rest. */
  ambiguous: boolean;
  /**
   * Every truck of the ZD has entries and this line is entered on none: it
   * goes on no truck (server warning SUPPLIER_CARGO_LINE_UNASSIGNED).
   */
  onNoTruck: boolean;
}

const EMPTY_SET: QuantitySet = { pcs: 0, m2: 0, m3: 0 };

function lineUnit(raw: unknown, set: QuantitySet): SupplierCargoUnit | null {
  const code = normalizeUnitCode(typeof raw === 'string' ? raw : null);
  if (code === 'pcs' || code === 'm2' || code === 'm3') return code;
  return UNITS.find((unit) => (getQuantitySetValue(set, unit) ?? 0) > 0) ?? null;
}

function availableUnits(set: QuantitySet): SupplierCargoUnit[] {
  return UNITS.filter((unit) => (getQuantitySetValue(set, unit) ?? 0) > 0);
}

function carriedToSet(carried: TransportSupplierOrderCarriedDto | null | undefined): QuantitySet | null {
  if (!carried) return null;
  return { pcs: carried.quantityPcs, m2: carried.quantityM2, m3: carried.quantityM3 };
}

function dimensionsOf(item: {
  width?: number | null; height?: number | null; length?: number | null; thickness?: number | null;
  dimensionDisplayProfile?: string | null;
}): string | null {
  return formatProductDimensions({
    width: item.width ?? undefined,
    height: item.height ?? undefined,
    length: item.length ?? undefined,
    thickness: item.thickness ?? undefined,
    dimensionDisplayProfile: item.dimensionDisplayProfile ?? null,
  }) || null;
}

function clampSet(set: QuantitySet): QuantitySet {
  return UNITS.reduce<QuantitySet>((result, unit) => {
    const value = getQuantitySetValue(set, unit);
    result[unit] = value === null ? null : roundCommercialQuantity(Math.max(value, 0));
    return result;
  }, { pcs: null, m2: null, m3: null });
}

function isZeroSet(set: QuantitySet): boolean {
  return UNITS.every((unit) => !((getQuantitySetValue(set, unit) ?? 0) > 0));
}

// The other trucks' entries are summed in the line's unit; re-express them
// through the line's own set so every unit the line has stays known.
function contextOf(context: SupplierCargoZdContext | null | undefined, itemId: string, ordered: QuantitySet) {
  if (!context) return { otherExplicit: null, otherRestTrucks: 0 };
  const entered = context.otherExplicitByItemId[itemId];
  const scaled = entered && entered.quantity !== null && isAllocationUnit(entered.unit)
    ? scaleQuantitySetToAmount(ordered, entered.quantity, entered.unit)
    : null;
  return {
    otherExplicit: entered ? (scaled ?? carriedToSet(entered) ?? EMPTY_SET) : EMPTY_SET,
    otherRestTrucks: context.restTruckLabels.length,
  };
}

function finiteOrNull(value: unknown): number | null {
  const numeric = Number(value);
  return value === null || value === undefined || value === '' || !Number.isFinite(numeric) ? null : numeric;
}

/** „poz. N”: the persisted lineNumber, else the 1-based place in the list. */
function linePosition(lineNumber: number | null | undefined, index: number): number {
  return typeof lineNumber === 'number' && Number.isInteger(lineNumber) && lineNumber > 0 ? lineNumber : index + 1;
}

/** Editor rows of a ZD already on this truck (edit mode: detail `loadItems`). */
export function linesFromLoadItems(
  supplierOrder: TransportSupplierOrderSummaryDto,
  context: SupplierCargoZdContext | null | undefined,
): SupplierCargoLine[] {
  return (supplierOrder.loadItems || []).map((item: TransportSupplierOrderLoadItemDto, index) => {
    const ordered = resolveStoredQuantitySet(item as unknown as Record<string, unknown>);
    const unit = lineUnit(item.unit, ordered);
    // The stored entry (`entered`) wins over `carried`: a delivered truck's
    // `carried` is reduced by what did not arrive and must not be saved back.
    const enteredLine = item.entered && isAllocationUnit(item.entered.unit) ? item.entered : null;
    const explicit = enteredLine
      ? { quantity: Number(enteredLine.quantity) || 0, unit: enteredLine.unit as SupplierCargoUnit }
      : item.entered === undefined && item.carriedSource === 'EXPLICIT' && item.carried && isAllocationUnit(item.carried.unit)
        ? { quantity: Number(item.carried.quantity) || 0, unit: item.carried.unit as SupplierCargoUnit }
        : null;
    return {
      supplierOrderId: supplierOrder.id,
      supplierOrderLabel: supplierOrder.displayOrderNumber || supplierOrder.orderNumber,
      itemId: item.id,
      position: linePosition(item.lineNumber, index),
      productVariantId: item.productVariantId ?? null,
      sourceOrderId: supplierOrder.draftSourceOrderId ?? null,
      name: item.name,
      dimensions: dimensionsOf(item),
      unit,
      units: availableUnits(ordered),
      keyUnit: item.unit,
      ordered,
      ...contextOf(context, item.id, ordered),
      savedExplicit: explicit,
      ...(item.carried !== undefined ? { savedCarried: item.carriedSource === 'NONE' ? EMPTY_SET : carriedToSet(item.carried) } : {}),
      width: item.width,
      height: item.height,
      length: item.length,
      thickness: item.thickness,
      countInPiecesOnly: item.countInPiecesOnly === true,
      pieceWeightKg: item.pieceWeightKg ?? context?.pieceWeightKgByItemId?.[item.id] ?? null,
    };
  });
}

/** Freight preview uses the server's delivered load; editor/payload keep entered quantities. */
export function supplierCargoFreightSet(
  line: SupplierCargoLine,
  state: SupplierCargoLineState | undefined,
  status: string | null | undefined,
): QuantitySet | null {
  if (status === 'DELIVERED' && line.savedCarried !== undefined) return line.savedCarried;
  return state?.carried ?? null;
}

/** Editor rows of a ZD that is new on this truck (from the ZD list item). */
export function linesFromSupplierOrder(
  supplierOrder: SupplierOrderDto,
  context: SupplierCargoZdContext | null,
): SupplierCargoLine[] {
  return (supplierOrder.items || []).map((item, index) => {
    const ordered = resolveStoredQuantitySet(item as unknown as Record<string, unknown>);
    return {
      supplierOrderId: supplierOrder.id,
      supplierOrderLabel: getSupplierOrderSummary(supplierOrder).displayNumber,
      itemId: item.id,
      position: linePosition(item.lineNumber, index),
      productVariantId: item.productVariantId ?? null,
      sourceOrderId: supplierOrder.draftSourceOrderId ?? null,
      name: item.catalogProductName || item.name,
      dimensions: dimensionsOf({
        width: finiteOrNull(item.width),
        height: finiteOrNull(item.height),
        length: finiteOrNull(item.length),
        thickness: finiteOrNull(item.thickness),
        dimensionDisplayProfile: item.productCategoryDimensionDisplayProfile ?? null,
      }),
      unit: lineUnit(item.unit, ordered),
      units: availableUnits(ordered),
      keyUnit: item.purchaseUnit || item.unit || null,
      ordered,
      ...contextOf(context, item.id, ordered),
      savedExplicit: null,
      width: finiteOrNull(item.width),
      height: finiteOrNull(item.height),
      length: finiteOrNull(item.length),
      thickness: finiteOrNull(item.thickness),
      countInPiecesOnly: item.countInPiecesOnly === true,
      pieceWeightKg: context?.pieceWeightKgByItemId?.[item.id] ?? null,
    };
  });
}

/** Value of a set in one unit, rounded for display; null = unknown. */
export function setValue(set: QuantitySet | null, unit: SupplierCargoUnit | null): number | null {
  if (!set || !unit) return null;
  const value = getQuantitySetValue(set, unit);
  return value === null ? null : roundCommercialQuantity(value);
}

/** The amount in `unit` re-expressed in `target` through the line's own set. */
export function convertLineAmount(
  line: SupplierCargoLine,
  amount: number,
  from: SupplierCargoUnit,
  target: SupplierCargoUnit,
): number | null {
  if (from === target) return amount;
  const scaled = scaleQuantitySetToAmount(line.ordered, amount, from);
  return scaled ? setValue(scaled, target) : null;
}

/** The positive quantity entered for the line on this truck (entry, else saved row). */
function enteredAmount(
  line: SupplierCargoLine,
  entry?: SupplierCargoEntry,
): { quantity: number; unit: SupplierCargoUnit } | null {
  const explicit = entry ? (entry.quantity === null ? null : entry) : line.savedExplicit;
  return explicit && explicit.quantity !== null && explicit.quantity > 0
    ? { quantity: explicit.quantity, unit: explicit.unit }
    : null;
}

/** True when the line will be stored explicitly after the save. */
export function isExplicit(line: SupplierCargoLine, entry?: SupplierCargoEntry): boolean {
  if (entry) return entry.quantity !== null;
  return line.savedExplicit !== null;
}

/**
 * The rule above for every line, given the operator's entries. A ZD is
 * "listed" on this truck when at least one of its lines has an entered
 * quantity > 0.
 */
export function resolveSupplierCargoLines(
  lines: SupplierCargoLine[],
  entries: Record<string, SupplierCargoEntry>,
): Map<string, SupplierCargoLineState> {
  const listed = new Set(lines
    .filter((line) => enteredAmount(line, entries[line.itemId]) !== null)
    .map((line) => line.supplierOrderId));
  return new Map(lines.map((line) => {
    const entered = enteredAmount(line, entries[line.itemId]);
    const free = line.otherExplicit === null
      ? line.ordered
      : clampSet(subtractQuantitySets(line.ordered, line.otherExplicit));
    const isListed = listed.has(line.supplierOrderId);
    const carried = isListed
      ? (entered ? (scaleQuantitySetToAmount(line.ordered, entered.quantity, entered.unit) ?? free) : { ...EMPTY_SET })
      : free;
    // Trucks of this ZD without entries (this one included when unlisted):
    // exactly one takes the rest; two or more = AMBIGUOUS (not assigned).
    const restTrucks = line.otherRestTrucks + (isListed ? 0 : 1);
    const rest = isListed ? clampSet(subtractQuantitySets(free, carried)) : free;
    const restResolved = restTrucks === 1 && !isZeroSet(rest);
    const ambiguous = !isListed && restTrucks > 1 && !isZeroSet(rest);
    const onOtherTrucks = line.otherExplicit === null
      ? null
      : (isListed && restResolved ? sumQuantitySets([line.otherExplicit, rest]) : line.otherExplicit);
    const unassigned = line.otherExplicit === null ? null : (restTrucks === 1 ? { ...EMPTY_SET } : rest);
    const onNoTruck = line.otherExplicit !== null && isListed && restTrucks === 0 && !entered
      && isZeroSet(line.otherExplicit) && !isZeroSet(line.ordered);
    return [line.itemId, {
      carried,
      onOtherTrucks,
      unassigned,
      notOnTruck: isListed && !entered,
      ambiguous,
      onNoTruck,
    }];
  }));
}

/**
 * Usability round 2026-10-05: the editor shows the real amount of every line
 * (not a placeholder). Typing one line makes the ZD's list explicit, so the
 * untouched lines of the same ZD keep what they showed (their current
 * default) instead of silently dropping to „nie jedzie tym autem”.
 */
/**
 * What the ZD lines this truck carries weigh: catalog weight of one piece ×
 * pieces carried (the trailer of the ZD editor and the 22 800 kg save question,
 * plan 2026-10-13). `withoutWeight` = carried lines without a piece weight.
 */
export function supplierCargoWeight(
  lines: SupplierCargoLine[],
  entries: Record<string, SupplierCargoEntry>,
): { kg: number; withoutWeight: number; byLine: Array<{ line: SupplierCargoLine; pieces: number; kg: number | null }> } {
  const states = resolveSupplierCargoLines(lines, entries);
  const byLine = lines.map((line) => {
    const pieces = getQuantitySetValue(states.get(line.itemId)?.carried ?? { pcs: 0, m2: 0, m3: 0 }, 'pcs') ?? 0;
    return { line, pieces, kg: line.pieceWeightKg ? pieces * line.pieceWeightKg : null };
  });
  return {
    kg: byLine.reduce((sum, item) => sum + (item.kg ?? 0), 0),
    withoutWeight: byLine.filter((item) => item.pieces > 0 && item.kg === null).length,
    byLine,
  };
}

export function applySupplierCargoEntry(
  lines: SupplierCargoLine[],
  previous: Record<string, SupplierCargoEntry>,
  itemId: string,
  entry: SupplierCargoEntry,
): Record<string, SupplierCargoEntry> {
  const next = { ...previous, [itemId]: entry };
  const line = lines.find((candidate) => candidate.itemId === itemId);
  if (!line || entry.quantity === null) return next;
  const states = resolveSupplierCargoLines(lines, previous);
  lines
    .filter((sibling) => sibling.supplierOrderId === line.supplierOrderId && sibling.itemId !== itemId)
    .filter((sibling) => !previous[sibling.itemId] && !sibling.savedExplicit && sibling.unit)
    // Review PR #269 (money): another truck takes this line's rest without
    // entered quantities — freezing it here would move goods and freight
    // between trucks without the operator's decision. Such a line stays
    // unentered (shows 0 / „nie jedzie tym autem”).
    .filter((sibling) => sibling.otherRestTrucks === 0 && !states.get(sibling.itemId)?.ambiguous)
    .forEach((sibling) => {
      const amount = setValue(states.get(sibling.itemId)?.carried ?? null, sibling.unit);
      if (amount !== null && amount > 0) next[sibling.itemId] = { quantity: amount, unit: sibling.unit as SupplierCargoUnit };
    });
  return next;
}

/**
 * Owner 2026-10-10 („Wszystko / Część / Nic”): „Nic” — this line does not
 * ride this truck. In the rule above a line is off the truck when its ZD is
 * listed (another line entered) and the line itself has no entry, so the
 * siblings keep what they show (as applySupplierCargoEntry) and this line's
 * entry is cleared. Returns null when no sibling would stay listed (a one-line
 * ZD or every other line off) — then the ZD itself leaves the truck instead.
 */
export function excludeSupplierCargoLine(
  lines: SupplierCargoLine[],
  previous: Record<string, SupplierCargoEntry>,
  itemId: string,
): Record<string, SupplierCargoEntry> | null {
  const line = lines.find((candidate) => candidate.itemId === itemId);
  if (!line?.unit) return null;
  const states = resolveSupplierCargoLines(lines, previous);
  const next: Record<string, SupplierCargoEntry> = { ...previous, [itemId]: { quantity: null, unit: line.unit } };
  let listed = false;
  lines
    .filter((sibling) => sibling.supplierOrderId === line.supplierOrderId && sibling.itemId !== itemId && sibling.unit)
    .forEach((sibling) => {
      const entered = enteredAmount(sibling, previous[sibling.itemId]);
      if (entered) { listed = true; return; }
      if (sibling.otherRestTrucks > 0 || states.get(sibling.itemId)?.ambiguous) return;
      const amount = setValue(states.get(sibling.itemId)?.carried ?? null, sibling.unit);
      if (amount !== null && amount > 0) {
        next[sibling.itemId] = { quantity: amount, unit: sibling.unit as SupplierCargoUnit };
        listed = true;
      }
    });
  return listed ? next : null;
}

/** Entered here + entered on other trucks over the ZD line beyond the tolerance. */
export function isOverOrdered(line: SupplierCargoLine, entry?: SupplierCargoEntry): boolean {
  const entered = enteredAmount(line, entry);
  const unit = line.unit;
  if (!entered || !unit) return false;
  const ordered = setValue(line.ordered, unit);
  const scaled = scaleQuantitySetToAmount(line.ordered, entered.quantity, entered.unit);
  const here = scaled ? setValue(scaled, unit) : null;
  const elsewhere = setValue(line.otherExplicit ?? EMPTY_SET, unit) ?? 0;
  if (ordered === null || here === null) return false;
  return here + elsewhere > ordered + CARGO_QUANTITY_TOLERANCE + 1e-6;
}

/**
 * `supplierCargoItems` of the save: every line that ends up explicit on this
 * truck (saved rows the operator kept + rows the operator entered), for the
 * ZDs still on the truck. Returns `undefined` when nothing was touched — the
 * key is then omitted and the server keeps the stored rows (contract: omitted
 * = keep, [] = clear).
 */
export function buildSupplierCargoPayload(
  lines: SupplierCargoLine[],
  entries: Record<string, SupplierCargoEntry>,
  touched: boolean,
  selectedSupplierOrderIds: string[],
): SupplierCargoItemPayload[] | undefined {
  if (!touched) return undefined;
  const selected = new Set(selectedSupplierOrderIds);
  return lines
    .filter((line) => selected.has(line.supplierOrderId))
    .flatMap((line) => {
      const entry = entries[line.itemId];
      const explicit = entry ? (entry.quantity === null ? null : entry) : line.savedExplicit;
      if (!explicit || explicit.quantity === null || !(explicit.quantity > 0)) return [];
      return [{
        supplierOrderItemId: line.itemId,
        quantity: roundCommercialQuantity(explicit.quantity),
        unit: explicit.unit,
      }];
    });
}

/**
 * Create-mode key availability positions of an INBOUND truck: the ZD lines it
 * carries (entered quantity, else the rest), in the line's purchase unit.
 */
export function supplierCargoPositions(
  lines: SupplierCargoLine[],
  entries: Record<string, SupplierCargoEntry>,
): AllocationKeyPosition[] {
  const states = resolveSupplierCargoLines(lines, entries);
  return lines.flatMap((line) => {
    const set = states.get(line.itemId)?.carried ?? EMPTY_SET;
    const keyUnit = normalizeUnitCode(line.keyUnit);
    const unit = [keyUnit, 'pcs', 'm2', 'm3']
      .find((candidate): candidate is SupplierCargoUnit => (
        candidate === 'pcs' || candidate === 'm2' || candidate === 'm3'
      ) && getQuantitySetValue(set, candidate) !== null);
    const quantity = unit ? getQuantitySetValue(set, unit) : null;
    if (!unit || !(Number(quantity) > 0)) return [];
    return [{
      productName: line.name,
      unit,
      quantity,
      m2: set.m2,
      m3: set.m3,
      width: line.width,
      height: line.height,
      length: line.length,
      thickness: line.thickness,
      countInPiecesOnly: line.countInPiecesOnly,
      source: 'ZD' as const,
    }];
  });
}
